// src/App.jsx
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Critical components loaded immediately (needed for initial render)
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import ConfirmEmail from "./pages/ConfirmEmail";
import ProtectedRoute from "./components/ProtectedRoute";
import RequirePermission from "./components/RequirePermission";
import SmartRedirect from "./components/SmartRedirect";
import ErrorBoundary from "./components/ErrorBoundary";
// Preload Layout and commonStyles to prevent webpack chunk loading errors
// These are used by almost all pages, so importing them here ensures they're in the main bundle
// This prevents webpack from creating malformed shared chunks with invalid names like:
// "src_components_Layout_jsx-src_styles_commonStyles_js"
import Layout from "./components/Layout";
import * as _commonStyles from "./styles/commonStyles";

// Ensure these are included in the main bundle (prevent tree-shaking)
// They're used by lazy-loaded pages, so we need them available immediately
if (process.env.NODE_ENV === 'development') {
  // In development, this helps webpack understand these should be in main bundle
  window.__layoutPreload = Layout;
  window.__commonStylesPreload = _commonStyles;
}

// Lazy-loaded components (code splitting by route)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const InvoiceCreate = lazy(() => import("./pages/InvoiceCreate"));
const Invoices = lazy(() => import("./pages/Invoices"));
const PackingListCreate = lazy(() => import("./pages/PackingListCreate"));
const AddressBook = lazy(() => import("./pages/AddressBook"));
const Banks = lazy(() => import("./pages/Banks"));
const ReconciliationIssues = lazy(() => import("./pages/ReconciliationIssues"));
const GST = lazy(() => import("./pages/GST"));
const IRM = lazy(() => import("./pages/IRM"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const IRMUtilized = lazy(() => import("./pages/IRMUtilized"));
const Support = lazy(() => import("./pages/Support"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const AWBCreate = lazy(() => import("./pages/AWBCreate"));
const Extensions = lazy(() => import("./pages/Extensions"));
const Accounting = lazy(() => import("./pages/Accounting"));
const ExportDocuments = lazy(() => import("./pages/ExportDocuments"));
const EBRC = lazy(() => import("./pages/EBRC"));
const EdpmsReconciliation = lazy(() => import("./pages/EdpmsReconciliation"));
const AmazonTax = lazy(() => import("./pages/AmazonTax"));
const MarketplaceSettlements = lazy(() => import("./pages/MarketplaceSettlements"));
const ActivityLog = lazy(() => import("./pages/ActivityLog"));
const CourierReconciliation = lazy(() => import("./pages/CourierReconciliation"));
const LegalPolicyPage = lazy(() => import("./pages/legal/LegalPolicyPage"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: '#0B1220',
    color: '#fff'
  }}>
    <div style={{
      fontSize: 18,
      color: '#94a3b8',
      marginBottom: 8
    }}>
      Loading...
    </div>
    <div style={{
      fontSize: 12,
      color: '#6b7280'
    }}>
      Please wait while we load the page
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          <Route path="/" element={<SmartRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/privacy-policy" element={<LegalPolicyPage />} />
          <Route path="/security" element={<LegalPolicyPage />} />
          <Route path="/data-retention" element={<LegalPolicyPage />} />
          <Route path="/incident-response" element={<LegalPolicyPage />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Sub-user gates. Packing-list routes are deliberately NOT wrapped: they
                double as the open/print page for an existing list (see RequirePermission). */}
            <Route
              path="/invoice/create"
              element={
                <RequirePermission permission="invoice.create" backTo="/invoices" backLabel="Back to invoices">
                  <InvoiceCreate />
                </RequirePermission>
              }
            />
            <Route
              path="/invoice/edit/:id"
              element={
                <RequirePermission permission="invoice.edit" backTo="/invoices" backLabel="Back to invoices">
                  <InvoiceCreate />
                </RequirePermission>
              }
            />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/packing-list/create" element={<PackingListCreate />} />
            <Route path="/packing-list/edit/:id" element={<PackingListCreate />} />
            <Route path="/address-book" element={<AddressBook />} />
            <Route path="/banks" element={<Banks />} />
            <Route path="/reconciliation-issues" element={<ReconciliationIssues />} />
            <Route path="/gst" element={<GST />} />
            <Route path="/irm" element={<IRM />} />
            <Route path="/edpms-reconciliation" element={<EdpmsReconciliation />} />
            <Route path="/irm-utilized" element={<IRMUtilized />} />
            <Route path="/ebrc" element={<EBRC />} />
            <Route path="/awb/create" element={<AWBCreate />} />
            <Route path="/extensions" element={<Extensions />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/support" element={<Support />} />
            <Route path="/accounting" element={<Accounting />} />
            <Route path="/export-documents" element={<ExportDocuments />} />
            <Route path="/amazon-tax" element={<AmazonTax />} />
            <Route path="/marketplace-settlements" element={<MarketplaceSettlements />} />
            <Route path="/activity" element={<ActivityLog />} />
            <Route path="/courier-reconciliation" element={<CourierReconciliation />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
